// pages/CategoryAlert.js
import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/apiInterceptor';
import SearchHistory from '../components/SearchHistory';
import { useSearchHistory } from '../hooks/useSearchHistory';
import { useApiData } from '../hooks/useApiData';
import { useFilters } from '../hooks/useFilters';
import { useHistoryHandler } from '../hooks/useHistoryHandler';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon,
  ClearAll as ClearAllIcon
} from '@mui/icons-material';
import { API_URL } from '../constants/data';
import {
  Box,
  CircularProgress,
  Alert,
  Chip,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  Button,
  Toolbar as MuiToolbar,
  Stack,
  TableSortLabel,
  useTheme, useMediaQuery,
  TablePagination,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useLocalStorage } from '../hooks/useLocalStorage';
import AlertFilterPanel from '../components/AlertFilterPanel';

const CACHE_EXPIRATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

const AlertPage = () => {
  const [alertData, setAlertData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState('category')
  const [orderBy, setOrderBy] = useState('category');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { filters, handleFilterChange, updateFilters } = useFilters();
  const [stats, setStats] = useState({
    totalCategories: 0,
    highAlerts: 0,
    mediumAlerts: 0,
    lowAlerts: 0
  });

  const [alertFilters, setAlertFilters] = useState({
    query: filters.query || '',
    reference_date: filters.startDate ? new Date(filters.startDate) : new Date(),
    thresold: 1.3
  });

  useEffect(() => {
    setAlertFilters(prev => ({
      ...prev,
      query: filters.query || prev.query,
      reference_date: filters.startDate ? new Date(filters.startDate) : prev.reference_date,
    }));
  }, [filters.query, filters.startDate]);

  const getAccessToken = () => {
    return localStorage.getItem("access_token") || "";
  }

  const theme = useTheme();
  const {
    totalCounts,
    setTotalCounts,
    isLoading,
    setIsLoading,
    fetchCategories: fetchCategoriesApi,
    fetchSemanticRCA,
    fetchUserData,
    fetchSpatialData,
    stateData,
    setStateData,
  } = useApiData();

  const handleClearSearch = () => {
    setSearchTerm('');
  };
  const { searchHistory, saveToHistory, clearHistory, deleteHistoryItem, selectedHistoryIndex, setSelectedHistoryIndex } = useSearchHistory();
  const { handleHistoryClick } = useHistoryHandler({
    fetchSemanticRCA,
    fetchSpatialData,
    fetchUserData,
    setTotalCounts,
    setIsLoading
  });

  const filteredData = useMemo(() => {
    if (!searchTerm) return alertData;
    return alertData.filter(row =>
      row[filterField]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [alertData, searchTerm, filterField]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const rows = useMemo(() => (
    alertData.map((row, index) => ({ id: index, ...row }))
  ), [alertData]);

  const sortAlertData = (data) => {
    return [...data].sort((a, b) => {
      const alertLevelA = getAlertLevel(a.increase_percentage);
      const alertLevelB = getAlertLevel(b.increase_percentage);

      // Define priority order for alert levels
      const priority = {
        'high': 1,
        'medium': 2,
        'low': 3
      };

      // Sort by priority first
      if (priority[alertLevelA] !== priority[alertLevelB]) {
        return priority[alertLevelA] - priority[alertLevelB];
      }

      // If same priority, sort by percentage (descending)
      return b.increase_percentage - a.increase_percentage;
    });
  };

  const [cachedData, setCachedData] = useLocalStorage('alertDataCache', {
    data: null,
    timestamp: null
  });

  const isCacheValid = () => {
    if (!cachedData.timestamp) return false;
    const now = Date.now();
    return (now - cachedData.timestamp) < CACHE_EXPIRATION;
  };



  const sortedData = useMemo(() => {
    const sorted = [...filteredData].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle string values
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();

      if (order === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
    return sorted;
  }, [filteredData, orderBy, order]);

  // Paginated data
  const paginatedData = useMemo(() => {
    return sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };



  const handleExport = () => {
    const csv = [
      ['Category', 'Previous Count', 'Current Count', 'Increase %', 'Pult Alert Level'],
      ...sortedData.map(row => [
        row.category,
        row.previous_count_per_day,
        row.current_count_per_day,
        row.increase_percentage.toFixed(1),
        getAlertLevel(row.increase_percentage).toUpperCase()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Alerts.csv';
    a.click();
  };

  const fetchCategoryAlerts = async (forceRefresh = false) => {
    if (!forceRefresh && isCacheValid() && cachedData.data) {
      const sortedData = sortAlertData(cachedData.data);
      setAlertData(sortedData);
      updateStats(sortedData);
      // setAlertData(cachedData.data);
      // updateStats(cachedData.data);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let date = alertFilters.reference_date
        ? new Date(alertFilters.reference_date)
        : new Date();

      // date.setMonth(date.getMonth() - 4);

      const formattedDate = date.toISOString().split('T')[0];

      const response = await api.post(
        `${API_URL}/get_alerts`,
        {
          reference_date: formattedDate,
          thresold: alertFilters.thresold,
          query: alertFilters.query
        }
      );

      const data = Array.isArray(response.data) ? response.data : (response.data.alerts || []);
      const sortedData = sortAlertData(data);
      setAlertData(sortedData);
      updateStats(sortedData);

      // Explicitly NOT caching this data in localStorage to avoid replacing dashboard cache
    } catch (err) {
      setError('Failed to fetch category alerts. ' + (err.response?.data?.message || err.message));
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStats = (data) => {
    const totalCategories = data.length;
    const highAlerts = data.filter(item => item.increase_percentage > 20).length;
    const mediumAlerts = data.filter(item => item.increase_percentage > 10 && item.increase_percentage <= 20).length;
    const lowAlerts = data.filter(item => item.increase_percentage <= 10).length;

    setStats({
      totalCategories,
      highAlerts,
      mediumAlerts,
      lowAlerts
    });
  };

  const getAlertLevel = (percentage) => {
    if (percentage > 20) return 'high';
    if (percentage > 10) return 'medium';
    return 'low';
  };

  const getAlertColor = (level) => {
    switch (level) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      default: return 'success';
    }
  };
  const cardHeight = {
    xs: '50px',
    sm: '70px',
    md: '80px',
    lg: '90px',
    xl: '100px'
  };

  const labelFontSize = {
    xs: '0.7rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.6rem',
    xl: '2rem'
  };

  const valueFontSize = {
    xs: '0.95rem',
    sm: '1.25rem',
    md: '1.75rem',
    lg: '2rem',
    xl: '2.2rem'
  };

  const padding = {
    xs: 0.75,
    sm: 1.5,
    md: 2,
    lg: 2.5,
    xl: 3
  };

  const spacing = {
    xs: 1.5,
    sm: 2,
    md: 2,
    lg: 3,
    xl: 6
  };

  const gap = {
    xs: 0.75,
    sm: 1.5,
    md: 2,
    lg: 2,
    xl: 2.5
  };

  const cardConfigs = [
    // {
    //   title: 'Total Categories',
    //   value: stats.totalCategories,
    //   gradient: '#c6e1ff',
    //   shadowColor: "rgba(157, 236, 252, 0.2)",
    //   // hoverShadowColor: 'rgba(33, 150, 243, 0.2)',
    //   textColor: '#444',
    // },
    {
      title: 'High Alerts',
      value: stats.highAlerts,
      gradient: '#f5dad6ff',
      shadowColor: "rgba(224, 157, 103, 0.2)",
      // hoverShadowColor: 'rgba(244, 67, 54, 0.2)',
      textColor: '#444',
      valueColor: '#444',
    },
    {
      title: 'Medium Alerts',
      value: stats.mediumAlerts,
      gradient: '#FCE7B8',
      shadowColor: "rgba(255, 231, 184, 0.2)",
      // hoverShadowColor: 'rgba(255, 193, 7, 0.2)',
      textColor: '#444',
      valueColor: '#444',
    },
    {
      title: 'Normal Alerts',
      value: stats.lowAlerts,
      gradient: '#D0CEFB',
      shadowColor: "rgba(158, 128, 197, 0.2)",
      // hoverShadowColor: 'rgba(76, 175, 80, 0.2)',
      textColor: '#444',
      valueColor: '#444',
    },
  ];


  const checkSubcategoryCache = () => {
    try {
      const cached = JSON.parse(localStorage.getItem("currentSubcategoryData") || "{}");
      if (cached?.prompt && cached?.timestamp && cached?.data) {
        const age = (Date.now() - cached.timestamp) / (1000 * 60 * 60);
        if (age < 24) {
          return cached.query || cached.prompt;
        }
      }
    } catch (error) {
      console.warn("Failed to check subcategory cache:", error);
    }
    return null;
  };

  useEffect(() => {
    const cachedQuery = checkSubcategoryCache();
    const cachedData = JSON.parse(localStorage.getItem("subAlertCache") || "{}");

    if (cachedQuery && cachedData?.prompt === cachedQuery && cachedData?.data && cachedData?.data?.length > 0) {
      const age = (Date.now() - cachedData.timestamp) / (1000 * 60 * 60);
      if (age < 24) {
        const data = sortAlertData(cachedData.data);
        setAlertData(data);
        updateStats(data);
        return;
      }
    }

    fetchCategoryAlerts();
  }, []);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("currentSubcategoryData"));


    if (!stored) return;

    const categories = stored.data.categories || {};
    const totalCategories = Object.keys(categories).length;

    setStats(prev => ({
      ...prev,
      totalCategories,
    }));


  }, []);





  return (
    <Fade in timeout={500}>
      <Box sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >

          <SearchHistory
            searchHistory={searchHistory}
            onHistoryClick={handleHistoryClick}
            onClearHistory={clearHistory}
            onDeleteHistoryItem={deleteHistoryItem}
            selectedHistoryIndex={selectedHistoryIndex}
            setSelectedHistoryIndex={setSelectedHistoryIndex}
          />
        </Box>

        {/* Alert Filter Panel */}
        <Box sx={{ mb: 1 }}>
          <AlertFilterPanel
            alertFilters={alertFilters}
            setAlertFilters={setAlertFilters}
            onApplyFilters={() => fetchCategoryAlerts(true)}
            isLoading={loading}
          />
        </Box>
        {/* 
        <Grid container spacing={spacing} sx={{ mb: { xs: 1.5, sm: 2, md: 2, lg: 2, xl: 2 }, display: 'flex', alignItems: 'center', justifyContent: 'start', paddingX: { xs: 2, sm: 2, md: 2, lg: 2, xl: 2 } }}>
          {cardConfigs.map((config, index) => (
            <Grid item xs={12} sm={6} md={6} lg={3} key={index}>
              <Card
                sx={{
                  height: cardHeight,
                  width: { xs: '100%', sm: '100%', md: '100%', lg: '100%', xl: '390px' },
                  background: config.gradient,
                  borderRadius: 2,
                  boxShadow: `0 2px 16px 0 ${config.shadowColor}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 4px 20px 0 ${config.hoverShadowColor}`,
                  }
                }}
              >
                <CardContent
                  sx={{
                    width: '100%',
                    height: '100%',
                    p: padding,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: gap,
                    '&:last-child': {
                      pb: padding,
                    },
                  }}
                >
                  {/* Label */}
        {/* <Typography
                    variant="subtitle2"
                    sx={{
                      color: config.textColor,
                      fontWeight: 600,
                      fontSize: labelFontSize,
                      lineHeight: 1.2,
                      flex: { xs: 1, sm: 1 },
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {config.title}
                  </Typography> */}

        {/* Value */}
        {/* <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: valueFontSize,
                      color: config.valueColor || 'inherit',
                      whiteSpace: 'nowrap',
                      minWidth: 'max-content',
                      lineHeight: 1,
                    }}
                  >
                    {config.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid> */}


        <Box sx={{ pX: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          <TableContainer component={Paper} sx={{ position: 'relative' }}>
            {/* Unified Control Bar */}
            <Box sx={{
              p: { xs: 1.5, sm: 2 },
              display: 'flex',
              gap: { xs: 1, sm: 1.5, md: 2 },
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: { xs: 'wrap', lg: 'nowrap' },
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
            }}>
              {/* Search Bar */}
              <TextField
                placeholder="Search alerts by category name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{
                  flexGrow: 1,
                  minWidth: { xs: '100%', sm: '200px', lg: '250px' },
                  order: { xs: 1, lg: 1 },
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={sortedData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  flexShrink: 0,
                  order: { xs: 2, lg: 2 },
                  minWidth: { xs: '100%', sm: 'auto' },
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                  overflow: 'hidden',
                  '& .MuiTablePagination-toolbar': {
                    minHeight: '38px',
                    height: '38px',
                    padding: 0,
                    paddingLeft: { xs: 1, sm: 1.5 },
                    paddingRight: { xs: 0.5, sm: 1 },
                  },
                  '& .MuiTablePagination-spacer': {
                    flex: '0 0 auto',
                  },
                  '& .MuiTablePagination-selectLabel': {
                    margin: 0,
                    fontSize: '0.875rem',
                    display: { xs: 'none', sm: 'block' },
                  },
                  '& .MuiTablePagination-select': {
                    paddingTop: '4px',
                    paddingBottom: '4px',
                    paddingLeft: '4px',
                    paddingRight: '4px',
                    fontSize: '0.875rem',
                  },
                  '& .MuiTablePagination-selectIcon': {
                    top: 'calc(50% - 12px)',
                  },
                  '& .MuiTablePagination-displayedRows': {
                    margin: 0,
                    fontSize: '0.875rem',
                  },
                  '& .MuiTablePagination-actions': {
                    marginLeft: { xs: 0.5, sm: 1 },
                    '& .MuiIconButton-root': {
                      padding: '8px',
                    }
                  }
                }}
              />

              {/* Export Button */}
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExport}
                sx={{
                  flexShrink: 0,
                  height: '40px',
                  minWidth: { xs: '100%', sm: 'auto' },
                  order: { xs: 3, lg: 3 },
                  textTransform: 'none',
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                  '&:hover': {
                    borderColor: 'rgba(0, 0, 0, 0.87)',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                Export
              </Button>
            </Box>

            {loading && (
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 10,
              }}>
                <CircularProgress />
              </Box>
            )}

            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', textAlign: 'left' }}>
                    <TableSortLabel
                      active={true}
                      direction={orderBy === 'category' ? order : 'asc'}
                      onClick={() => handleSort('category')}
                    >
                      Alerts Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', textAlign: 'center' }}>
                    <TableSortLabel
                      active={true}
                      direction={orderBy === 'previous_count_per_day' ? order : 'asc'}
                      onClick={() => handleSort('previous_count_per_day')}
                    >
                      Previous Count per Day
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', textAlign: 'center' }}>
                    <TableSortLabel
                      active={true}
                      direction={orderBy === 'current_count_per_day' ? order : 'asc'}
                      onClick={() => handleSort('current_count_per_day')}
                    >
                      Current Count per Day
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', textAlign: 'center' }}>
                    <TableSortLabel
                      active={true}
                      direction={orderBy === 'increase_percentage' ? order : 'asc'}
                      onClick={() => handleSort('increase_percentage')}
                    >
                      Increase %
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    Alert level
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((row, index) => {
                    const alertLevel = getAlertLevel(row.increase_percentage);

                    return (
                      <TableRow
                        key={index}
                        sx={{
                          '&:hover': { backgroundColor: '#f5f5f5' },
                          '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }
                        }}
                      >
                        <TableCell sx={{ textAlign: 'left' }}>{row.category}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>{row.previous_count_per_day}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>{row.current_count_per_day}</TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            color: `${getAlertColor(alertLevel)}.main`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1
                          }}
                        >
                          <TrendingUpIcon />
                          {row.increase_percentage.toFixed(1)}%
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={alertLevel.toUpperCase()}
                            color={getAlertColor(alertLevel)}
                            variant={alertLevel === 'high' ? 'filled' : 'outlined'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                      No Alerts found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Fade>
  );
};

export default AlertPage;